type FindingSeverity = 'error' | 'warning';

interface CountableFinding<CheckName extends string> {
    check: CheckName;
    severity: FindingSeverity;
}

/**
 * Mixpanel properties counting how many findings each check raised, keyed `${severity}_${check}`
 * (check ids snake_cased), plus `total_errors`/`total_warnings`. Every check that ran is listed
 * under each severity it can raise, at zero when it found nothing, so a check that never fires is
 * still visible as one that ran clean. A finding at a severity its check didn't declare is still
 * counted.
 */
export function buildCheckFindingCounts<CheckName extends string>(
    checksRun: readonly CheckName[],
    findings: readonly CountableFinding<CheckName>[],
    severitiesOf: (check: CheckName) => readonly FindingSeverity[],
): Record<string, number> {
    const propertyName = (severity: FindingSeverity, check: CheckName) =>
        `${severity}_${check.replaceAll('-', '_')}`;

    const counts: Record<string, number> = Object.fromEntries(
        checksRun.flatMap((check) =>
            severitiesOf(check).map((severity) => [propertyName(severity, check), 0]),
        ),
    );
    for (const finding of findings) {
        const key = propertyName(finding.severity, finding.check);
        counts[key] = (counts[key] ?? 0) + 1;
    }

    return {
        ...counts,
        total_errors: findings.filter((finding) => finding.severity === 'error').length,
        total_warnings: findings.filter((finding) => finding.severity === 'warning').length,
    };
}
